import type { HttpEvent } from '@angular/common/http';
import { HttpResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs/operators';

import { RecentlyViewedService } from '../../dashboard/services/recently-viewed.service';
import type { IRecentlyViewed } from '../models';
import { apiUrlToId } from '../utils/api-url-to-id.util';

interface IRequestHandlerOption {
  urlPathRegex: RegExp;
  apiURL: string;
  entityProp: string;
  bookmarkProp: string;
}

// The index signature carries `bookmarkProp`, which is looked up by name.
interface IViewedEntity {
  id: number;
  title?: string;
  name?: string;
  url?: string;
  is_ticket?: boolean;
  [key: string]: unknown;
}

const options: IRequestHandlerOption[] = [
  {
    urlPathRegex: new RegExp(/^\/api\/monitoringtopic\/(\d+)\/?$/),
    apiURL: '/api/monitoringtopic',
    entityProp: 'monitoringtopic',
    bookmarkProp: 'monitoringtopic_bookmarked',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/table\/(\d+)\/?$/),
    apiURL: '/api/table',
    entityProp: 'table',
    bookmarkProp: 'table_bookmarked',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/issue\/(\d+)\/?$/),
    apiURL: '/api/issue?is_ticket=0',
    entityProp: 'issue',
    bookmarkProp: 'issue_bookmarked',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/issue\/(\d+)\/?$/),
    apiURL: '/api/issue?is_ticket=1',
    entityProp: 'issue',
    bookmarkProp: 'issue_bookmarked',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/project\/(\d+)\/?$/),
    apiURL: '/api/project?detail_serializer=1',
    entityProp: 'project',
    bookmarkProp: 'project_bookmarked',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/process\/(\d+)\/?$/),
    apiURL: '/api/process',
    entityProp: 'process',
    bookmarkProp: 'process_bookmarked',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/report\/(\d+)\/?$/),
    apiURL: '/api/report',
    entityProp: 'report',
    bookmarkProp: 'report_bookmarked',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/task\/(\d+)\/?$/),
    apiURL: '/api/task',
    entityProp: 'task',
    bookmarkProp: 'task_bookmarked',
  },
];

const getOptionURL = (option: IRequestHandlerOption, response: { is_ticket?: unknown }) => {
  switch (option.apiURL) {
    case '/api/issue?is_ticket=0':
    case '/api/issue?is_ticket=1':
      return response.is_ticket ? '/api/issue?is_ticket=1' : '/api/issue?is_ticket=0';
    default:
      return option.apiURL;
  }
};

const isViewedEntity = (value: unknown): value is IViewedEntity =>
  typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'number';

const toRecentlyViewed = (body: IViewedEntity, option: IRequestHandlerOption, requestUrl: string, isTask: boolean): IRecentlyViewed =>
  isTask
    ? { ...body, title: body.title ?? '', url: body.url ?? requestUrl, entityProp: 'task' }
    : { id: body.id, title: body.title || body.name || '', url: requestUrl, entityProp: option.entityProp };

export const recentlyViewedInterceptor: HttpInterceptorFn = (request, next) => {
  const recentlyViewedService = inject(RecentlyViewedService);
  const { url } = request;
  const option = options.find(({ urlPathRegex }) => urlPathRegex.test(url));
  if (option) {
    return next(request).pipe(
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse && [200, 204].includes(event.status)) {
          try {
            const { body } = event;
            const isTask = option.apiURL.includes('task');

            if (isViewedEntity(body) && request.method === 'GET') {
              if (!body[option.bookmarkProp]) {
                recentlyViewedService.addItem(getOptionURL(option, body), toRecentlyViewed(body, option, url, isTask));
              } else {
                recentlyViewedService.removeItem(option.apiURL, body.id);
              }
            }
            if (isViewedEntity(body) && request.method === 'PUT') {
              if (!body[option.bookmarkProp]) {
                recentlyViewedService.updateItem(getOptionURL(option, body), body.id, toRecentlyViewed(body, option, url, isTask));
              } else {
                recentlyViewedService.removeItem(option.apiURL, body.id);
              }
            }
            if (request.method === 'DELETE') {
              const id = apiUrlToId(request.url);
              recentlyViewedService.removeItem(option.apiURL, parseInt(id ?? ''));
            }
          } catch {
            new Error('Cannot manage recently viewed item');
          }
        }
      }),
    );
  }
  return next(request);
};
